/**
 * AP-35 Nachtrag 17 (Detailblatt 5 5.3b a, d) — **die Marke des Einkaufs an der Stücklistenzeile**, in der Stückliste
 * und im Einkauf dieselbe.
 *
 * - offen, angefragt: was die Lieferanten angeboten haben — „845,00 übernehmen · 2 Angebote, bestes von X" (der Knopf
 *   vorn, wie AP-39 PE-124: abgeschnitten wird nur der Text), übernommen, gelesen, nicht angeboten, angefragt bei;
 * - bestellt, bestätigt: was der Lieferant bestätigt hat — AB fehlt seit n Tagen, gelesen, bestätigt für, Termin +n
 *   Tage, nach dem Liefertermin der Position, ohne Termin, Menge, Preis.
 *
 * Die Handlungen kommen über den `EinkaufsKontext` — die Zeile liegt tief im Zeilenblock. Fehlt eine, steht die Marke
 * als Text. Gerechnet wird nichts: Die Marke kommt fertig vom Server (`offer`, `confirmation`).
 */
import { createContext, useContext, useLayoutEffect, useRef, type MouseEvent, type ReactNode } from 'react'
import { preisText } from '@symphenia/shared-types'
import { useTerminology } from '@/lib/useTerminology'
import type { BomLine } from '../api/project-bom.api'
import { alsTag } from '../project-labels'

export interface EinkaufsAktionen {
  /** „845,00 übernehmen" — die Vorschau für diese Zeile mit dem besten Angebot */
  uebernehmen?: (z: BomLine) => void
  /** der Text der Angebotsmarke — alle Angebote im Fenster bzw. im Preisspiegel */
  vergleichen?: (z: BomLine) => void
  /** Verweis auf einen Beleg (Preisanfrage, Bestellung) */
  oeffneBeleg?: (documentId: string) => void
}

export const EinkaufsKontext = createContext<EinkaufsAktionen>({})

/** Die Farben der Lagen — grün wie die Antwort des Lieferanten (AP-39 N2 4.), violett wie der Vorschlag der KI. */
const FARBE = {
  angebot: 'font-medium text-emerald-700 dark:text-emerald-400',
  gelesen: 'font-medium text-violet-700 dark:text-violet-300',
  hinweis: 'text-amber-700 dark:text-amber-400',
  abweichung: 'font-medium text-destructive',
  leise: 'text-muted-foreground',
}

/** Ein Knopf in der Marke: nimmt beim Drücken keinen Fokus (die Zeile klappte sonst auf, Nachtrag 15) und hält die Markierung. */
function Knopf({ className, onClick, title, children }: { className: string; onClick: () => void; title?: string; children: ReactNode }) {
  return (
    <button
      type="button"
      tabIndex={-1}
      data-auswahl-behalten
      className={`${className} hover:underline`}
      title={title}
      onMouseDown={(e: MouseEvent) => e.preventDefault()}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      {children}
    </button>
  )
}

const anzahl = (n: number, eins: string, viele: string) => `${n} ${n === 1 ? eins : viele}`

/** Was angeboten ist — `null`, wenn die Zeile keine Anfrage hat. */
export function AngebotsMarke({ z }: { z: BomLine }) {
  const { uebernehmen, vergleichen, oeffneBeleg } = useContext(EinkaufsKontext)
  const m = z.offer
  if (!m) return null
  const teile: ReactNode[] = []
  if (m.state === 'OFFER' && m.best) {
    const b = m.best
    // Gilt das beste Angebot je anderer Einheit, sagt der Knopf es so, wie es übernommen wird (wie AP-39 PE-126).
    const anders = b.conversion !== 'KEINE' && b.bestOfferPrice != null && !!b.offerUnitCode
    const betrag = anders ? `${preisText(b.bestOfferPrice!, 4)} je ${b.offerUnitCode}` : b.bestNetPrice != null ? preisText(b.bestNetPrice, 4) : null
    if (betrag && uebernehmen) {
      teile.push(
        <Knopf
          key="uebernehmen"
          className={FARBE.angebot}
          onClick={() => uebernehmen(z)}
          title={anders && b.bestNetPrice != null ? `je ${z.unit?.code ?? 'Einheit'} der Zeile: ${preisText(b.bestNetPrice, 4)} — die Zeile wird auf ${b.offerUnitCode} umgestellt` : `Angebot ${b.documentNumber} übernehmen`}
        >
          {betrag} übernehmen
        </Knopf>,
      )
    }
    const text = `${anzahl(m.count, 'Angebot', 'Angebote')}, bestes von ${b.supplierName}`
    teile.push(
      vergleichen ? (
        <Knopf key="text" className={teile.length ? FARBE.leise : FARBE.angebot} onClick={() => vergleichen(z)} title="alle Angebote vergleichen">
          {text}
        </Knopf>
      ) : (
        <span key="text" className={teile.length ? FARBE.leise : FARBE.angebot}>
          {text}
        </span>
      ),
    )
  } else if (m.state === 'OFFER') {
    // Kein Angebot in einer vergleichbaren Einheit — es gibt nur den Vergleich.
    const text = anzahl(m.count, 'Angebot', 'Angebote')
    teile.push(vergleichen ? <Knopf key="text" className={FARBE.angebot} onClick={() => vergleichen(z)}>{text}</Knopf> : <span key="text" className={FARBE.angebot}>{text}</span>)
  } else if (m.state === 'APPLIED') {
    const text = `Angebot${m.appliedSupplierName ? ` von ${m.appliedSupplierName}` : ''} übernommen${m.count > 1 ? ` · ${m.count - 1} weitere` : ''}`
    teile.push(vergleichen ? <Knopf key="text" className={FARBE.leise} onClick={() => vergleichen(z)}>{text}</Knopf> : <span key="text" className={FARBE.leise}>{text}</span>)
  } else {
    if (m.readCount > 0 && m.readDocumentId) {
      const text = `Angebot von ${m.readSupplierName ?? 'Lieferant'} gelesen — an der Preisanfrage bestätigen`
      teile.push(
        oeffneBeleg ? (
          <Knopf key="gelesen" className={FARBE.gelesen} onClick={() => oeffneBeleg(m.readDocumentId!)}>
            {text}
          </Knopf>
        ) : (
          <span key="gelesen" className={FARBE.gelesen}>
            {text}
          </span>
        ),
      )
    }
    if (m.requestedFrom.length) teile.push(<span key="angefragt" className={FARBE.leise}>angefragt bei {m.requestedFrom.join(', ')}</span>)
    if (m.notOfferedBy.length) {
      // Allein heißt „nicht angeboten": Es kommt nichts mehr — die Zeile braucht einen anderen Lieferanten (PE-123).
      teile.push(
        teile.length ? (
          <span key="nicht" className={FARBE.leise}>nicht angeboten: {m.notOfferedBy.join(', ')}</span>
        ) : (
          <span key="nicht" className={FARBE.hinweis}>nicht angeboten ({m.notOfferedBy.join(', ')})</span>
        ),
      )
    }
  }
  return <Kette teile={teile} />
}

/** Was der Lieferant bestätigt hat — `null`, solange die Zeile nicht bestellt ist. */
export function BestaetigungsMarke({ z }: { z: BomLine }) {
  const { oeffneBeleg } = useContext(EinkaufsKontext)
  const terms = useTerminology()
  const m = z.confirmation
  if (!m) return null
  const zumBeleg = (key: string, className: string, text: string, title?: string) =>
    oeffneBeleg ? (
      <Knopf key={key} className={className} onClick={() => oeffneBeleg(m.documentId)} title={title}>
        {text}
      </Knopf>
    ) : (
      <span key={key} className={className} title={title}>
        {text}
      </span>
    )
  const teile: ReactNode[] = []
  if (m.state === 'MISSING') {
    teile.push(
      zumBeleg(
        'fehlt',
        FARBE.hinweis,
        `AB fehlt${m.sinceDays ? ` · seit ${anzahl(m.sinceDays, 'Tag', 'Tagen')}` : ''}`,
        `Die Auftragsbestätigung wird an der Bestellung ${m.documentNumber ?? ''} erfasst`.replace('  ', ' '),
      ),
    )
  } else if (m.state === 'READ') {
    teile.push(zumBeleg('gelesen', FARBE.gelesen, `AB von ${m.supplierName} gelesen — an der Bestellung bestätigen`))
  } else {
    const ab = m.externalNumber ? `AB ${m.externalNumber}` : undefined
    // Die roten Hinweise zuerst: Breit schneidet die Kurzinfo die Marke ab — weg fällt dann nur die Datumsangabe.
    if (m.lateDays) {
      teile.push(
        <span key="termin" className={FARBE.abweichung} title={ab}>
          Termin +{anzahl(m.lateDays, 'Tag', 'Tage')}
        </span>,
      )
    } else if (m.deliveryAt) {
      teile.push(
        <span key="termin" className={m.state === 'CONFIRMED' ? FARBE.angebot : FARBE.leise} title={ab}>
          bestätigt für {alsTag(m.deliveryAt)}
        </span>,
      )
    }
    if (m.positionDueAt) {
      teile.push(
        <span key="position" className={FARBE.abweichung}>
          nach dem Liefertermin der {terms.position} ({alsTag(m.positionDueAt)})
        </span>,
      )
    }
    if (m.lateDays && m.deliveryAt) {
      teile.push(
        <span key="statt" className={FARBE.leise}>
          {alsTag(m.deliveryAt)}
          {m.wishedAt ? ` statt ${alsTag(m.wishedAt)}` : ''}
        </span>,
      )
    }
    if (m.noDate) teile.push(<span key="ohne" className={FARBE.hinweis} title={ab}>bestätigt ohne Liefertermin</span>)
    if (m.quantityText) teile.push(<span key="menge" className={FARBE.hinweis}>{m.quantityText}</span>)
    if (m.price) teile.push(<span key="preis" className={FARBE.hinweis}>{m.price === 'HIGHER' ? 'Preis höher' : 'Preis niedriger'}</span>)
    if (m.read) teile.push(zumBeleg('neu', FARBE.gelesen, 'neue AB gelesen — an der Bestellung bestätigen'))
  }
  return <Kette teile={teile} />
}

/** Angebot oder Bestätigung — je nachdem, was die Zeile trägt. */
export function EinkaufsMarke({ z }: { z: BomLine }) {
  if (z.confirmation) return <BestaetigungsMarke z={z} />
  if (z.offer) return <AngebotsMarke z={z} />
  return null
}

function Kette({ teile }: { teile: ReactNode[] }) {
  const ref = useRef<HTMLSpanElement>(null)
  // Der ganze Text als Kurzhinweis — breit steht die Marke in einer abgeschnittenen Zeile (Nachtrag 15, N15-8).
  useLayoutEffect(() => {
    if (ref.current) ref.current.title = ref.current.textContent ?? ''
  })
  if (!teile.length) return null
  return (
    <span data-einkaufsmarke ref={ref}>
      {teile.map((t, i) => (
        <span key={i}>
          {i > 0 && <span className="text-muted-foreground"> · </span>}
          {t}
        </span>
      ))}
    </span>
  )
}
